'use client'

import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { useI18n } from '@/lib/i18n'
import { gameQueries, type FilterGame, type GameData } from '@/lib/queries'
import {
  FilterOptionsResponse,
  GameDataResponse,
  GamesByFiltersResponse,
  trpcClient,
} from '@/lib/trpc'
import {
  ChevronDown,
  ChevronRight,
  ChevronUp,
  Loader2,
  RefreshCw,
} from 'lucide-react'
import * as React from 'react'
import { useCallback, useEffect, useState } from 'react'

// Note: Filter options are now loaded dynamically from the database

export function QuerySection() {
  const { t } = useI18n()
  const [gameId, setGameId] = useState('')
  const [searchTerm, setSearchTerm] = useState('')
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage] = useState(20)
  const [expandedSections, setExpandedSections] = useState<{
    godLog: boolean
    systemPrompt: boolean
    [key: string]: boolean
  }>({
    godLog: false,
    systemPrompt: false,
  })
  const [sortField, setSortField] = useState<
    'roundNumber' | 'createdAt' | null
  >(null)
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc')
  const [filterResults, setFilterResults] = useState<FilterGame[]>([])
  const [showFilterResults, setShowFilterResults] = useState(false)
  const [filterResultsPage, setFilterResultsPage] = useState(1)
  const filterResultsPerPage = 10
  const [filterUserId, setFilterUserId] = useState('')
  const [filterGameId, setFilterGameId] = useState('')
  const [selectedFilters, setSelectedFilters] = useState<{
    languages: string[]
    statuses: string[]
    models: string[]
    tags: Array<'game' | 'data'>
  }>({
    languages: [],
    statuses: [],
    models: [],
    tags: [],
  })
  const [isFiltering, setIsFiltering] = useState(false)
  const [filterOptions, setFilterOptions] = useState<{
    languages: string[]
    statuses: string[]
    models: string[]
  }>({
    languages: [],
    statuses: [],
    models: [],
  })
  const [isLoadingFilterOptions, setIsLoadingFilterOptions] = useState(true)

  const [gameData, setGameData] = useState<GameData>({
    game: null,
    players: [],
    actions: [],
  })
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<Error | null>(null)

  // Load filter options on component mount
  useEffect(() => {
    const loadFilterOptions = async () => {
      try {
        const result: FilterOptionsResponse
          = await gameQueries.getFilterOptions()
        if (result.success) {
          setFilterOptions(result.data)
        }
      }
      catch {
        // Handle error silently
      }
      finally {
        setIsLoadingFilterOptions(false)
      }
    }

    loadFilterOptions()
  }, [])

  const toggleExpanded = (section: string) => {
    setExpandedSections((prev) => {
      const newState = {
        ...prev,
        [section]: !prev[section],
      }

      // If expanding, scroll to the expanded element after a short delay
      if (!prev[section] && typeof window !== 'undefined') {
        setTimeout(() => {
          // Find the expanded element and scroll it to the top of viewport
          const expandedElement
            = typeof globalThis !== 'undefined' && globalThis.window
              ? globalThis.window.document?.querySelector(
                  `[data-section="${section}"]`,
                )
              : null
          if (expandedElement) {
            expandedElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            })
          }
        }, 100)
      }

      return newState
    })
  }

  const formatContent = (content: string) => {
    if (!content)
      return 'No content'
    // Try to format as JSON if it looks like JSON
    try {
      const parsed = JSON.parse(content)
      return JSON.stringify(parsed, null, 2)
    }
    catch {
      return content
    }
  }

  const resetAllExpanded = () => {
    setExpandedSections({
      godLog: false,
      systemPrompt: false,
    })
  }

  const handleSort = (field: 'roundNumber' | 'createdAt') => {
    if (sortField === field) {
      // Toggle direction if same field
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc')
    }
    else {
      // Set new field with ascending direction
      setSortField(field)
      setSortDirection('asc')
    }
  }

  // Debounced filter function
  const debouncedFilter = useCallback(
    async (
      filters: typeof selectedFilters,
      userId: string,
      gameIdFilter: string,
    ) => {
      const hasFilters
        = filters.languages.length > 0
          || filters.statuses.length > 0
          || filters.models.length > 0
          || filters.tags.length > 0
          || userId.trim().length > 0
          || gameIdFilter.trim().length > 0

      if (!hasFilters) {
        setShowFilterResults(false)
        return
      }

      setIsFiltering(true)
      try {
        const result: GamesByFiltersResponse
          = await gameQueries.getGamesByFilters({
            ...filters,
            ...(userId.trim() ? { userId: userId.trim() } : {}),
            ...(gameIdFilter.trim() ? { gameId: gameIdFilter.trim() } : {}),
          })
        if (result.success) {
          setFilterResults(result.data)
          setFilterResultsPage(1)
          setShowFilterResults(true)
          setGameId('') // Clear the game ID input
        }
      }
      catch (error) {
        console.error('Filter error:', error)
      }
      finally {
        setIsFiltering(false)
      }
    },
    [],
  )

  // Debounce effect
  useEffect(() => {
    const timer = setTimeout(() => {
      debouncedFilter(selectedFilters, filterUserId, filterGameId)
    }, 500) // 500ms delay

    return () => clearTimeout(timer)
  }, [selectedFilters, filterUserId, filterGameId, debouncedFilter])

  const handleFilterToggle = (
    filterType: 'languages' | 'statuses' | 'models' | 'tags',
    filterValue: string,
  ) => {
    const value = filterValue as 'game' | 'data'
    setSelectedFilters(prev => ({
      ...prev,
      [filterType]:
        filterType === 'tags'
          ? prev.tags.includes(value)
            ? prev.tags.filter(v => v !== value)
            : [...prev.tags, value]
          : prev[filterType].includes(filterValue)
            ? (prev[filterType] as string[]).filter(v => v !== filterValue)
            : [...(prev[filterType] as string[]), filterValue],
    }))
  }

  const clearAllFilters = () => {
    setFilterUserId('')
    setFilterGameId('')
    setSelectedFilters({
      languages: [],
      statuses: [],
      models: [],
      tags: [],
    })
    setShowFilterResults(false)
  }

  // 刷新缓存的处理函数
  const handleRefresh = async () => {
    if (!gameId.trim())
      return

    setIsLoading(true)
    setError(null)

    try {
      const result = await trpcClient.queryRouter.refreshAllCache.mutate()

      if (result && result.message) {
        console.log('Cache refreshed successfully:', result.message)
        // 刷新成功后，重新获取数据
        const gameDataResult: GameDataResponse = await gameQueries.getGameDataById(gameId)
        if (gameDataResult.success) {
          setGameData(gameDataResult.data)
        }
        else {
          setError(new Error(gameDataResult.error || 'Failed to refresh game data'))
        }
      }
      else {
        console.error('Unexpected result format:', result)
        setError(new Error('Failed to refresh cache'))
      }
    }
    catch (err) {
      console.error('Failed to refresh cache:', err)
      setError(err as Error)
    }
    finally {
      setIsLoading(false)
    }
  }

  const handleGameIdClick = async (
    gameId: string,
    keepFilterResults = false,
  ) => {
    setGameId(gameId)
    if (!keepFilterResults) {
      setShowFilterResults(false)
    }
    setCurrentPage(1)
    setSearchTerm('')
    setIsLoading(true)
    setError(null)

    // Immediately fetch and display the game data
    try {
      const result: GameDataResponse = await gameQueries.getGameDataById(gameId)
      if (result.success) {
        setGameData(result.data)

        // Auto-scroll to Game Information section after data is loaded
        setTimeout(() => {
          const gameInfoElement = document.querySelector(
            '[data-section="game-info"]',
          )
          if (gameInfoElement) {
            gameInfoElement.scrollIntoView({
              behavior: 'smooth',
              block: 'start',
              inline: 'nearest',
            })
          }
        }, 100)
      }
      else {
        setError(new Error(result.error || 'Failed to fetch game data'))
      }
    }
    catch (err) {
      setError(err as Error)
    }
    finally {
      setIsLoading(false)
    }
  }

  // Filter actions by search term across metadata fields (excluding input/output)
  const filteredActions = gameData.actions.filter((action) => {
    if (searchTerm.length < 3)
      return true // Don't filter if less than 3 characters

    const searchLower = searchTerm.toLowerCase()
    return (
      action.playerName.toLowerCase().includes(searchLower)
      || action.playerRole.toLowerCase().includes(searchLower)
      || action.roundNumber.toString().includes(searchLower)
      || action.phase.toLowerCase().includes(searchLower)
      || action.actionType.toLowerCase().includes(searchLower)
      || action.subPhase.toLowerCase().includes(searchLower)
      || new Date(action.createdAt)
        .toLocaleString()
        .toLowerCase()
        .includes(searchLower)
    )
  })

  // Sort filtered actions
  const sortedActions = [...filteredActions].sort((a, b) => {
    if (!sortField)
      return 0

    let comparison = 0
    if (sortField === 'roundNumber') {
      comparison = a.roundNumber - b.roundNumber
    }
    else if (sortField === 'createdAt') {
      comparison
        = new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
    }

    return sortDirection === 'asc' ? comparison : -comparison
  })

  // Paginate actions
  const totalPages = Math.ceil(sortedActions.length / itemsPerPage)
  const startIndex = (currentPage - 1) * itemsPerPage
  const paginatedActions = sortedActions.slice(
    startIndex,
    startIndex + itemsPerPage,
  )

  return (
    <div
      className="min-h-screen bg-slate-50 px-4 py-20 dark:bg-slate-900 sm:px-6 lg:px-8"
      onClick={resetAllExpanded}
    >
      <div className="mx-auto max-w-[95vw]">
        <div className="mb-12 text-center">
          <h2 className="mb-4 text-2xl font-bold text-foreground md:text-3xl lg:text-4xl">
            {t('admin.nav.query')}
          </h2>
          <p className="text-lg text-muted-foreground">
            {t('admin.query.description')}
          </p>
        </div>

        {/* Filter Buttons */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>{t('admin.query.quickFilters')}</CardTitle>
              <Button
                variant="outline"
                size="sm"
                onClick={clearAllFilters}
                disabled={selectedFilters.languages.length === 0
                  && selectedFilters.statuses.length === 0
                  && selectedFilters.models.length === 0
                  && selectedFilters.tags.length === 0
                  && !filterUserId.trim()
                  && !filterGameId.trim()}
              >
                {t('admin.query.clearAll')}
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* User ID & Game ID input filters */}
              <div className="grid gap-4 sm:grid-cols-2">
                <div>
                  <Label
                    htmlFor="filterUserId"
                    className="mb-1 block text-sm font-medium"
                  >
                    {t('admin.query.userIdLabel')}
                  </Label>
                  <Input
                    id="filterUserId"
                    value={filterUserId}
                    onChange={e => setFilterUserId(e.target.value)}
                    placeholder={t('admin.query.userIdPlaceholder')}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label
                    htmlFor="filterGameId"
                    className="mb-1 block text-sm font-medium"
                  >
                    {t('admin.query.filterGameIdLabel')}
                  </Label>
                  <Input
                    id="filterGameId"
                    value={filterGameId}
                    onChange={e => setFilterGameId(e.target.value)}
                    placeholder={t('admin.query.filterGameIdPlaceholder')}
                    className="mt-1"
                  />
                </div>
              </div>

              {/* Tag filter (game / data) */}
              <div>
                <h4 className="mb-2 text-sm font-medium">
                  {t('admin.query.filterByTag')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant={
                      selectedFilters.tags.includes('game') ? 'default' : 'outline'
                    }
                    size="sm"
                    onClick={() => handleFilterToggle('tags', 'game')}
                  >
                    {t('admin.query.tagGame')}
                  </Button>
                  <Button
                    variant={
                      selectedFilters.tags.includes('data') ? 'default' : 'outline'
                    }
                    size="sm"
                    onClick={() => handleFilterToggle('tags', 'data')}
                  >
                    {t('admin.query.tagData')}
                  </Button>
                </div>
              </div>

              {/* Language Filter */}
              <div>
                <h4 className="mb-2 text-sm font-medium">
                  {t('admin.query.filterByLanguage')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {isLoadingFilterOptions ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="size-4 animate-spin" />
                      Loading languages...
                    </div>
                  ) : (
                    filterOptions.languages.map(lang => (
                      <Button
                        key={lang}
                        variant={
                          selectedFilters.languages.includes(lang)
                            ? 'default'
                            : 'outline'
                        }
                        size="sm"
                        onClick={() => handleFilterToggle('languages', lang)}
                      >
                        {lang}
                      </Button>
                    ))
                  )}
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <h4 className="mb-2 text-sm font-medium">
                  {t('admin.query.filterByStatus')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {isLoadingFilterOptions ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="size-4 animate-spin" />
                      Loading statuses...
                    </div>
                  ) : (
                    filterOptions.statuses.map(status => (
                      <Button
                        key={status}
                        variant={
                          selectedFilters.statuses.includes(status)
                            ? 'default'
                            : 'outline'
                        }
                        size="sm"
                        onClick={() => handleFilterToggle('statuses', status)}
                      >
                        {status}
                      </Button>
                    ))
                  )}
                </div>
              </div>

              {/* Model Filter */}
              <div>
                <h4 className="mb-2 text-sm font-medium">
                  {t('admin.query.filterByModel')}
                </h4>
                <div className="flex flex-wrap gap-2">
                  {isLoadingFilterOptions ? (
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Loader2 className="size-4 animate-spin" />
                      Loading models...
                    </div>
                  ) : (
                    filterOptions.models.map(model => (
                      <Button
                        key={model}
                        variant={
                          selectedFilters.models.includes(model)
                            ? 'default'
                            : 'outline'
                        }
                        size="sm"
                        onClick={() => handleFilterToggle('models', model)}
                        className="text-xs"
                      >
                        {model}
                      </Button>
                    ))
                  )}
                </div>
              </div>

            </div>
            {isFiltering && (
              <div className="mt-4 flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="size-4 animate-spin" />
                {t('admin.query.filtering')}...
              </div>
            )}
          </CardContent>
        </Card>

        {/* Filter Results */}
        {showFilterResults && (
          <Card className="mb-8">
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle>
                  {t('admin.query.filterResults')} ({filterResults.length})
                </CardTitle>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowFilterResults(false)}
                >
                  {t('admin.query.close')}
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        {t('admin.query.gameId')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        {t('admin.query.status')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        {t('admin.query.gameSetting')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        {t('admin.query.goodModel')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        {t('admin.query.evilModel')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        {t('admin.query.createdAt')}
                      </th>
                      <th className="px-4 py-3 text-left font-semibold text-foreground">
                        {t('admin.query.action')}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filterResults
                      .slice(
                        (filterResultsPage - 1) * filterResultsPerPage,
                        filterResultsPage * filterResultsPerPage,
                      )
                      .map(game => (
                        <tr
                          key={game.id}
                          className="border-b border-border/50 hover:bg-muted/50"
                        >
                          <td className="px-4 py-3 font-mono text-sm text-foreground">
                            {game.id}
                          </td>
                          <td className="px-4 py-3 text-foreground">
                            {game.status}
                          </td>
                          <td className="px-4 py-3 text-foreground">
                            {game.gameSetting}
                          </td>
                          <td className="px-4 py-3 text-foreground">
                            {game.goodModel ?? '-'}
                          </td>
                          <td className="px-4 py-3 text-foreground">
                            {game.evilModel ?? '-'}
                          </td>
                          <td className="px-4 py-3 text-foreground">
                            {new Date(game.createdAt).toLocaleString()}
                          </td>
                          <td className="px-4 py-3 text-foreground">
                            <Button
                              size="sm"
                              onClick={() => handleGameIdClick(game.id, true)}
                            >
                              {t('admin.query.viewDetails')}
                            </Button>
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              {filterResults.length > filterResultsPerPage && (
                <div className="mt-4 flex items-center justify-between">
                  <div className="text-sm text-muted-foreground">
                    {t('admin.query.showing')}{' '}
                    {(filterResultsPage - 1) * filterResultsPerPage + 1} -{' '}
                    {Math.min(
                      filterResultsPage * filterResultsPerPage,
                      filterResults.length,
                    )}{' '}
                    {t('admin.query.of')} {filterResults.length}
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFilterResultsPage(prev => Math.max(prev - 1, 1))}
                      disabled={filterResultsPage <= 1}
                    >
                      {t('admin.query.previous')}
                    </Button>
                    <span className="text-sm text-foreground">
                      {filterResultsPage} /{' '}
                      {Math.ceil(
                        filterResults.length / filterResultsPerPage,
                      )}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() =>
                        setFilterResultsPage(prev =>
                          Math.min(
                            prev + 1,
                            Math.ceil(
                              filterResults.length / filterResultsPerPage,
                            ),
                          ),
                        )}
                      disabled={filterResultsPage
                        >= Math.ceil(
                          filterResults.length / filterResultsPerPage,
                        )}
                    >
                      {t('admin.query.next')}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Results */}
        {(gameData.game
          || gameData.players.length > 0
          || gameData.actions.length > 0) && (
          <div className="space-y-8" onClick={e => e.stopPropagation()}>
            {/* Game Information */}
            {gameData.game && (
              <Card data-section="game-info">
                <CardHeader>
                  <CardTitle>{t('admin.query.gameInfo')}</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-6">
                    {/* Basic Game Info */}
                    <div className="overflow-x-auto">
                      <table className="w-full border-collapse">
                        <thead>
                          <tr className="border-b border-border">
                            <th className="px-4 py-3 text-left font-semibold text-foreground">
                              {t('admin.query.field')}
                            </th>
                            <th className="px-4 py-3 text-left font-semibold text-foreground">
                              {t('admin.query.value')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {Object.entries(gameData.game)
                            .filter(
                              ([key]) =>
                                !['godLog', 'systemPrompt'].includes(key),
                            )
                            .map(([key, value]) => (
                              <tr
                                key={key}
                                className="border-b border-border/50"
                              >
                                <td className="px-4 py-3 font-medium text-foreground">
                                  {t(`admin.query.${key}`)}
                                </td>
                                <td className="px-4 py-3 text-foreground">
                                  {key === 'data' && value ? (
                                    <pre className="max-w-md overflow-auto rounded bg-muted p-2 text-xs">
                                      {JSON.stringify(value, null, 2)}
                                    </pre>
                                  ) : (
                                    String(value || '')
                                  )}
                                </td>
                              </tr>
                            ))}
                        </tbody>
                      </table>
                    </div>

                    {/* God Log */}
                    <div
                      className="rounded-lg border p-4"
                      data-section="godLog"
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleExpanded('godLog')
                        }}
                        className="flex w-full items-center gap-2 text-left font-semibold text-foreground hover:text-primary"
                      >
                        {expandedSections.godLog ? (
                          <ChevronDown className="size-4" />
                        ) : (
                          <ChevronRight className="size-4" />
                        )}
                        {t('admin.query.godLog')} (
                        {gameData.game.godLog?.length || 0} chars)
                      </button>
                      {expandedSections.godLog && (
                        <div className="mt-4">
                          <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded bg-muted p-4 text-sm">
                            {gameData.game.godLog || 'No content'}
                          </pre>
                        </div>
                      )}
                    </div>

                    {/* System Prompt */}
                    <div
                      className="rounded-lg border p-4"
                      data-section="systemPrompt"
                    >
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          toggleExpanded('systemPrompt')
                        }}
                        className="flex w-full items-center gap-2 text-left font-semibold text-foreground hover:text-primary"
                      >
                        {expandedSections.systemPrompt ? (
                          <ChevronDown className="size-4" />
                        ) : (
                          <ChevronRight className="size-4" />
                        )}
                        {t('admin.query.systemPrompt')} (
                        {gameData.game.systemPrompt?.length || 0} chars)
                      </button>
                      {expandedSections.systemPrompt && (
                        <div className="mt-4">
                          <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded bg-muted p-4 text-sm">
                            {formatContent(gameData.game.systemPrompt || '')}
                          </pre>
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Players Table */}
            <Card>
              <CardHeader>
                <CardTitle>
                  {t('admin.query.players')} ({gameData.players.length})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.position')}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.name')}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.role')}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.faction')}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.model')}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.status')}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.log')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {gameData.players.map(player => (
                        <React.Fragment key={player.id}>
                          <tr className="border-b border-border/50 hover:bg-muted/50">
                            <td className="px-4 py-3 text-foreground">
                              {player.position}
                            </td>
                            <td className="px-4 py-3 font-medium text-foreground">
                              {player.name}
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              {player.role}
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              {player.faction}
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              {player.modelName}
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              {player.status}
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleExpanded(`player-log-${player.id}`)
                                }}
                                className="flex items-center gap-1 text-left hover:text-primary"
                              >
                                {expandedSections[`player-log-${player.id}`] ? (
                                  <ChevronDown className="size-3" />
                                ) : (
                                  <ChevronRight className="size-3" />
                                )}
                                <span className="max-w-xs truncate">
                                  {player.log.length > 50
                                    ? `${player.log.substring(0, 50)}...`
                                    : player.log || 'No log'}
                                </span>
                              </button>
                            </td>
                          </tr>
                          {/* Expanded Player Log Row */}
                          {expandedSections[`player-log-${player.id}`] && (
                            <tr
                              className="border-b border-border/50"
                              data-section={`player-log-${player.id}`}
                            >
                              <td colSpan={7} className="p-0">
                                <div className="bg-muted/50 p-4">
                                  <div className="mb-2 flex items-center justify-between">
                                    <h4 className="font-semibold text-foreground">
                                      {t('admin.query.log')} - {player.name} (
                                      {player.role})
                                    </h4>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        toggleExpanded(
                                          `player-log-${player.id}`,
                                        )
                                      }}
                                      className="text-muted-foreground hover:text-foreground"
                                    >
                                      <ChevronDown className="size-4" />
                                    </button>
                                  </div>
                                  <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded border bg-background p-3 text-sm">
                                    {formatContent(
                                      player.log || 'No log available',
                                    )}
                                  </pre>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>

            {/* Actions Table with Search and Pagination */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle>
                    {t('admin.query.actions')} ({sortedActions.length})
                    {searchTerm.length >= 3 && (
                      <span className="ml-2 text-sm font-normal text-muted-foreground">
                        - {t('admin.query.filteredBy')} "{searchTerm}"
                      </span>
                    )}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={handleRefresh}
                      disabled={isLoading || !gameId.trim()}
                    >
                      {isLoading ? (
                        <>
                          <Loader2 className="mr-2 size-4 animate-spin" />
                          Refreshing...
                        </>
                      ) : (
                        <>
                          <RefreshCw className="mr-2 size-4" />
                          Refresh
                        </>
                      )}
                    </Button>
                    <Input
                      placeholder={t('admin.query.searchAll')}
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      className="w-64"
                    />
                    {searchTerm.length > 0 && searchTerm.length < 3 && (
                      <span className="text-sm text-muted-foreground">
                        {t('admin.query.minChars')}
                      </span>
                    )}
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <div className="overflow-x-auto">
                  <table className="w-full border-collapse">
                    <thead>
                      <tr className="border-b border-border">
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.player')}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.role')}
                        </th>
                        <th
                          className="cursor-pointer select-none px-4 py-3 text-left font-semibold text-foreground hover:bg-muted/50"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSort('roundNumber')
                          }}
                        >
                          <div className="flex items-center gap-1">
                            {t('admin.query.roundNumber')}
                            {sortField === 'roundNumber'
                              && (sortDirection === 'asc' ? (
                                <ChevronUp className="size-4" />
                              ) : (
                                <ChevronDown className="size-4" />
                              ))}
                          </div>
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.actionType')}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.subPhase')}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.input')}
                        </th>
                        <th className="px-4 py-3 text-left font-semibold text-foreground">
                          {t('admin.query.output')}
                        </th>
                        <th
                          className="cursor-pointer select-none px-4 py-3 text-left font-semibold text-foreground hover:bg-muted/50"
                          onClick={(e) => {
                            e.stopPropagation()
                            handleSort('createdAt')
                          }}
                        >
                          <div className="flex items-center gap-1">
                            {t('admin.query.createdAt')}
                            {sortField === 'createdAt'
                              && (sortDirection === 'asc' ? (
                                <ChevronUp className="size-4" />
                              ) : (
                                <ChevronDown className="size-4" />
                              ))}
                          </div>
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedActions.map(action => (
                        <React.Fragment key={action.id}>
                          <tr
                            key={action.id}
                            className="border-b border-border/50 hover:bg-muted/50"
                          >
                            <td className="px-4 py-3 font-medium text-foreground">
                              {action.playerName}
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              {action.playerRole}
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              {action.roundNumber}
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              {action.actionType}
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              {action.subPhase}
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleExpanded(`input-${action.id}`)
                                }}
                                className="flex items-center gap-1 text-left hover:text-primary"
                              >
                                {expandedSections[`input-${action.id}`] ? (
                                  <ChevronDown className="size-3" />
                                ) : (
                                  <ChevronRight className="size-3" />
                                )}
                                <span className="max-w-xs truncate">
                                  {action.input.length > 50
                                    ? `${action.input.substring(0, 50)}...`
                                    : action.input}
                                </span>
                              </button>
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  toggleExpanded(`output-${action.id}`)
                                }}
                                className="flex items-center gap-1 text-left hover:text-primary"
                              >
                                {expandedSections[`output-${action.id}`] ? (
                                  <ChevronDown className="size-3" />
                                ) : (
                                  <ChevronRight className="size-3" />
                                )}
                                <span className="max-w-xs truncate">
                                  {action.output.length > 50
                                    ? `${action.output.substring(0, 50)}...`
                                    : action.output}
                                </span>
                              </button>
                            </td>
                            <td className="px-4 py-3 text-foreground">
                              {new Date(action.createdAt).toLocaleString()}
                            </td>
                          </tr>
                          {/* Expanded Input Row */}
                          {expandedSections[`input-${action.id}`] && (
                            <tr
                              key={`input-${action.id}`}
                              className="border-b border-border/50"
                              data-section={`input-${action.id}`}
                            >
                              <td colSpan={9} className="p-0">
                                <div className="bg-muted/50 p-4">
                                  <div className="mb-2 flex items-center justify-between">
                                    <h4 className="font-semibold text-foreground">
                                      {t('admin.query.input')} -{' '}
                                      {action.playerName}
                                    </h4>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        toggleExpanded(`input-${action.id}`)
                                      }}
                                      className="text-muted-foreground hover:text-foreground"
                                    >
                                      <ChevronDown className="size-4" />
                                    </button>
                                  </div>
                                  <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded border bg-background p-3 text-sm">
                                    {formatContent(action.input)}
                                  </pre>
                                </div>
                              </td>
                            </tr>
                          )}
                          {/* Expanded Output Row */}
                          {expandedSections[`output-${action.id}`] && (
                            <tr
                              key={`output-${action.id}`}
                              className="border-b border-border/50"
                              data-section={`output-${action.id}`}
                            >
                              <td colSpan={9} className="p-0">
                                <div className="bg-muted/50 p-4">
                                  <div className="mb-2 flex items-center justify-between">
                                    <h4 className="font-semibold text-foreground">
                                      {t('admin.query.output')} -{' '}
                                      {action.playerName}
                                    </h4>
                                    <button
                                      onClick={(e) => {
                                        e.stopPropagation()
                                        toggleExpanded(`output-${action.id}`)
                                      }}
                                      className="text-muted-foreground hover:text-foreground"
                                    >
                                      <ChevronDown className="size-4" />
                                    </button>
                                  </div>
                                  <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap rounded border bg-background p-3 text-sm">
                                    {formatContent(action.output)}
                                  </pre>
                                </div>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="mt-4 flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">
                      {t('admin.query.showing')} {startIndex + 1} -{' '}
                      {Math.min(
                        startIndex + itemsPerPage,
                        sortedActions.length,
                      )}{' '}
                      {t('admin.query.of')} {sortedActions.length}
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage(prev => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                      >
                        {t('admin.query.previous')}
                      </Button>
                      <span className="text-sm text-foreground">
                        {currentPage} / {totalPages}
                      </span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() =>
                          setCurrentPage(prev =>
                            Math.min(prev + 1, totalPages),
                          )}
                        disabled={currentPage === totalPages}
                      >
                        {t('admin.query.next')}
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Error State */}
        {error && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-red-600">
                {t('admin.query.error')}: {error.message}
              </div>
            </CardContent>
          </Card>
        )}

        {/* No Data State */}
        {!gameData.game && !isLoading && !showFilterResults && (
          <Card>
            <CardContent className="pt-6">
              <div className="text-center text-muted-foreground">
                {t('admin.query.noData')}
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  )
}
